import { yupResolver } from "@hookform/resolvers/yup";
import dayjs from "dayjs";
import { type FC, useCallback, useEffect } from "react";
import { FormProvider, type UseFormReturn, useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import * as yup from "yup";

import { type Breadcrumb, useBreadcrumb } from "../../../components/Breadcrumb";
import { ButtonUI } from "../../../components/Button";
import { LoadingUI } from "../../../components/Loading";
import { PageTitle } from "../../../components/PageTitle";
import {
  EmailTemplateCategory,
  useEmailTemplatesRestApi,
} from "../../../hooks/useEmailTemplatesRestApi";
import { genMessageTagHash } from "../../../utils/hash-utils";
import { checkTemplateVariables } from "./checkTemplateVariables";
import type { FormData } from "./index";
import { type FormValues, SendBulkEmailsForm } from "./SendBulkEmailsForm";

const sendBulkEmailsSchema = yup.object().shape({
  category: yup.string().required("配信種別は必須です"),
  from: yup.string().required("配信者は必須です"),
  subject: yup.string().required("件名は必須です"),
  content: yup
    .string()
    .required("本文は必須です")
    .test(
      "valid-template-variables",
      "本文に不正な変数があります（使用できるのは {{NAME}}, {{COMPANY}} のみです）",
      checkTemplateVariables,
    ),
  scheduledAt: yup
    .string()
    .test("noWeekend", "土日を指定することはできません。", value => {
      if (!value || !dayjs(value).isValid()) return true; // 無効な日付はスキップ
      const day = dayjs(value).day(); // 0: 日曜日, 6: 土曜日
      return day !== 0 && day !== 6;
    })
    .test("withinBusinessHours", "予約可能時間は8:00〜20:00です。", value => {
      if (!value || !dayjs(value).isValid()) return true; // 無効な日付はスキップ
      const hour = dayjs(value).hour();
      return hour >= 8 && hour <= 20;
    }),
});

const BREAD_CRUMBS: Breadcrumb[] = [
  { label: "ホーム", to: "/" },
  { label: "メール一斉配信" },
];

// Propsインターフェースを定義
interface SendBulkEmailsPageProps {
  templateFormMethods: UseFormReturn<FormData>;
  isSubmitting: boolean;
  errorMessage: string;
  onSubmit: (data: FormData) => Promise<boolean>;
  clearMessages: () => void;
}

export const SendBulkEmailsPage: FC<SendBulkEmailsPageProps> = ({
  templateFormMethods,
  isSubmitting,
  errorMessage,
  onSubmit,
  clearMessages,
}) => {
  const { setBreadcrumbItems } = useBreadcrumb();
  const navigate = useNavigate();
  const location = useLocation();
  const initialFormValues = location.state as FormValues;

  const methods = useForm<FormValues>({
    defaultValues: initialFormValues || {
      category: EmailTemplateCategory.Hr,
      from: "",
      subject: "",
      content: "",
      hrHash: "",
      scheduled: false,
      scheduledAt: "",
    },
    resolver: yupResolver(sendBulkEmailsSchema),
  });
  const { data, loading } = useEmailTemplatesRestApi({
    filter: {
      isDefault: true,
    },
  });

  useEffect(() => {
    setBreadcrumbItems(BREAD_CRUMBS);
  }, [setBreadcrumbItems]);

  const handleFormSubmit = useCallback(
    (emailsInput: FormValues) => {
      navigate("/emails/bulk/confirm", {
        state: { ...emailsInput },
      });
    },
    [navigate],
  );

  const setGeneratedHash = useCallback(async () => {
    try {
      const hash = await genMessageTagHash();
      methods.setValue("hrHash", hash);
    } catch (_error) {
      // エラーを無視
    }
  }, [methods]);

  useEffect(() => {
    if (!methods.getValues().hrHash) {
      setGeneratedHash();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [methods.getValues, setGeneratedHash]);

  useEffect(() => {
    const scheduled = methods.watch("scheduled");
    if (!scheduled) {
      methods.setValue("scheduledAt", "");
    }
  }, [methods]);

  return (
    <div className="container mx-auto px-4 py-8">
      <PageTitle>メール一斉配信</PageTitle>
      <FormProvider {...methods}>
        <div className="max-w-5xl mx-auto">
          {loading ? (
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-12 flex justify-center">
              <LoadingUI type="Grid" />
            </div>
          ) : (
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 md:p-8">
              <SendBulkEmailsForm
                data={data!}
                hrHash={methods.watch("hrHash")}
                scheduled={methods.watch("scheduled")}
                onGenerateHash={setGeneratedHash}
                // テンプレート編集モーダル用のprops
                templateFormMethods={templateFormMethods}
                isTemplateSubmitting={isSubmitting}
                templateErrorMessage={errorMessage}
                onTemplateSubmit={onSubmit}
                clearTemplateMessages={clearMessages}
              />
              <div className="flex justify-end border-t border-gray-100 pt-6 mt-6">
                <ButtonUI
                  className="w-40"
                  type="submit"
                  onClick={methods.handleSubmit(handleFormSubmit)}
                >
                  確認画面へ
                </ButtonUI>
              </div>
            </div>
          )}
        </div>
      </FormProvider>
    </div>
  );
};
